/**
 * ExerciseField Component
 * Renders an exercise with selection and configuration using React Hook Form
 * Requirements: 4.2
 */

import * as React from 'react';
import {memo, useCallback, useState} from 'react';
import {
  ExercisePreviewContainer,
  ExercisePreviewHeader,
  ExerciseImageThumb,
  ExerciseInfo,
  ExerciseName,
  ExerciseDetailsGrid,
  ExerciseDetail,
  ExerciseActions,
  ExerciseActionButton,
  MoveButtons,
  ExerciseNotes,
} from '../../../../../components/exercises/ExercisePreview/ExercisePreview.style';
import {
  useExerciseField,
  formatDetailText,
  hasAnyDetails,
} from './ExerciseField.use-case';
import type {
  ExerciseFieldProps,
  SelectedExerciseData,
} from './ExerciseField.type';

/**
 * ExerciseField Component
 * Manages a single exercise with selection and configuration
 * Uses React.memo for performance optimization (Requirement 6.2)
 */
export const ExerciseField = memo(function ExerciseField({
  form,
  groupIndex,
  workoutIndex,
  exerciseIndex,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  exerciseListDefault = [],
}: ExerciseFieldProps): JSX.Element {
  const {exerciseData, isEditing, setIsEditing, handleUpdateExercise} =
    useExerciseField(form, groupIndex, workoutIndex, exerciseIndex);

  // State for exercise library modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get current exercise workout data for initial modal values
  const exerciseWorkout = form.watch(
    `workoutGroups.${groupIndex}.workouts.${workoutIndex}.exerciseWorkouts.${exerciseIndex}`,
  );

  // Handle edit button click
  const handleEdit = useCallback((event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setIsModalOpen(true);
  }, []);

  // Handle delete button click
  const handleDelete = useCallback(
    (event?: React.MouseEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      onRemove();
    },
    [onRemove],
  );

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Handle exercise update from modal
  const handleSelectExercise = useCallback(
    (data: SelectedExerciseData) => {
      handleUpdateExercise(data);
      setIsModalOpen(false);
    },
    [handleUpdateExercise],
  );

  // Get initial exercise data for modal
  const getInitialExerciseData = useCallback((): SelectedExerciseData => {
    // Cast to any to access properties that may exist at runtime but not in strict types
    const exercise = exerciseWorkout?.exercise as any;
    const workout = exerciseWorkout as any;

    const exerciseId =
      exerciseWorkout?.exerciseId || exercise?.value || exercise?.id || '';
    const exerciseName = exercise?.label || exercise?.name || '';

    return {
      exercise: {
        id: exerciseId,
        name: exerciseName,
        animatedImage: exercise?.animatedImage,
        image: exercise?.image,
      },
      series: workout?.rounds || workout?.series || '',
      reps: exerciseWorkout?.repetitions || workout?.reps || '',
      weight: exerciseWorkout?.load || workout?.weight || '',
      time: exerciseWorkout?.time || workout?.timeText || '',
      restTime: String(exerciseWorkout?.restTime || ''),
      notes: exerciseWorkout?.notes || '',
    };
  }, [exerciseWorkout]);

  // Render exercise details
  const renderDetails = () => {
    const {series, reps, weight, time, restTime} = exerciseData.details;
    const details = [
      formatDetailText('Séries', series),
      formatDetailText('Reps', reps),
      formatDetailText('Carga', weight),
      formatDetailText('Tempo', time),
      formatDetailText('Descanso', restTime),
    ].filter(Boolean);

    return details.map((detail, index) => (
      <ExerciseDetail key={index}>
        <span>{detail}</span>
      </ExerciseDetail>
    ));
  };

  // Dynamically import ExerciseLibraryModal to avoid circular dependencies
  const ExerciseLibraryModal = React.useMemo(() => {
    try {
      return require('../../../../../components/exercises/ExerciseLibraryModal')
        .ExerciseLibraryModal;
    } catch {
      return null;
    }
  }, []);

  const showDetails = hasAnyDetails(exerciseData.details);

  return (
    <>
      <ExercisePreviewContainer>
        <ExercisePreviewHeader>
          <MoveButtons>
            <i
              onClick={onMoveUp}
              className="far fa-long-arrow-up"
              title="Mover exercício para cima"
              style={{
                opacity: isFirst ? 0.3 : 1,
                cursor: isFirst ? 'not-allowed' : 'pointer',
              }}
            />
            <i
              onClick={onMoveDown}
              className="far fa-long-arrow-down"
              title="Mover exercício para baixo"
              style={{
                opacity: isLast ? 0.3 : 1,
                cursor: isLast ? 'not-allowed' : 'pointer',
              }}
            />
          </MoveButtons>

          <ExerciseImageThumb>
            {exerciseData.imageUrl ? (
              <img src={exerciseData.imageUrl} alt={exerciseData.name} />
            ) : (
              <i
                className="far fa-dumbbell"
                style={{fontSize: '1.5rem', color: '#999'}}
              />
            )}
          </ExerciseImageThumb>

          <ExerciseInfo>
            <ExerciseName>{exerciseData.name}</ExerciseName>
            {showDetails && (
              <ExerciseDetailsGrid>{renderDetails()}</ExerciseDetailsGrid>
            )}
          </ExerciseInfo>

          <ExerciseActions>
            <ExerciseActionButton
              type="button"
              className="edit"
              onClick={handleEdit}
              title="Editar exercício">
              <i className="far fa-edit" />
              Editar
            </ExerciseActionButton>

            <ExerciseActionButton
              type="button"
              className="delete"
              onClick={handleDelete}
              title="Remover exercício">
              <i className="far fa-trash" />
              Apagar
            </ExerciseActionButton>
          </ExerciseActions>
        </ExercisePreviewHeader>

        {exerciseData.details.notes && (
          <ExerciseNotes>
            <strong>Observações:</strong>
            {exerciseData.details.notes}
          </ExerciseNotes>
        )}
      </ExercisePreviewContainer>

      {/* Exercise Library Modal */}
      {ExerciseLibraryModal && (
        <ExerciseLibraryModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSelectExercise={handleSelectExercise}
          workoutGroupIndex={groupIndex}
          workoutIndex={workoutIndex}
          initialExerciseData={getInitialExerciseData()}
          isEditing={true}
        />
      )}
    </>
  );
});

export default ExerciseField;
